// Table column definitions for company data
// Company Sidebar Filter Constants
import { Industry_Verticals } from "../../../constants/namingConventions";
import { FilterToggleState, FilterType } from "../../CredentialApp/types/assignment-filters";
import { ICompanyColumn, NumberUnitType, SidebarFilter } from "../types/common";

export const RANGE_MIN = 0;
export const RANGE_MAX = 360;
export const NUMBER_RANGE_MAX = 1000000;
export const MONEY_RANGE_MAX = 5000000000000; // 5 trillion
export const RANGE_STEP = 3;
export const SINGLE_RANGE_STEP = 1;
export const MONEY_RANGE_STEP = 10000; // 10k
export const RANGE_YEAR = 12;
export const RANGE_SPLITTER = '-';
export const MILLION_UNITS = 1000000;

export type CompanyColumnId =
  | 'companyName'
  | 'lastFinancingDate'
  | 'lastFinancingSize'
  | 'lastFinancingDealType'
  | 'lastFinancingStatus'
  | 'activeInvestors'
  | 'primaryIndustrySector'
  | 'primaryIndustryGroup'
  | 'verticals'
  | 'descriptions'
  | 'hqLocation'
  | 'revenue'
  | 'marketCap'
  | 'profile'
  | 'ebitda'
  | 'companyFinancingStatus'
  | 'ownershipStatus';


export const TOOLTIP_ENABLED_COLUMNS: string[] = [
  'descriptions', 'primaryIndustrySector'
]

// All available columns for the company table
export const COMPANY_COLUMNS: ICompanyColumn[] = [
  {
    id: 'companyName',
    header: 'Company Name',
    accessorKey: 'companyName',
    defaultVisible: true,
    icon: 'business',
    dataType: 'string',
  },
  {
    id: 'lastFinancingDate',
    header: 'Last Financing Date',
    accessorKey: 'lastFinancingDate',
    defaultVisible: true,
    icon: 'event',
    dataType: 'date',
  },
  {
    id: 'lastFinancingSize',
    header: 'Last Financing Size',
    accessorKey: 'lastFinancingSize',
    defaultVisible: true,
    icon: 'straighten',
    dataType: 'money',
  },
  {
    id: 'lastFinancingDealType',
    header: 'Last Financing Deal Type',
    accessorKey: 'lastFinancingDealType',
    defaultVisible: true,
    icon: 'handshake',
    dataType: 'string',
  },
  {
    id: 'lastFinancingStatus',
    header: 'Last Financing Status',
    accessorKey: 'lastFinancingStatus',
    defaultVisible: true,
    icon: 'pending',
    dataType: 'string',
  },
  {
    id: 'activeInvestors',
    header: 'Active Investors',
    accessorKey: 'activeInvestors',
    defaultVisible: true,
    icon: 'badge',
    dataType: 'string',
  },
  {
    id: 'primaryIndustrySector',
    header: 'Primary Industry Sector',
    accessorKey: 'primaryIndustrySector',
    defaultVisible: true,
    icon: 'category',
    dataType: 'string',
  },
  {
    id: 'primaryIndustryGroup',
    header: 'Primary Industry Group',
    accessorKey: 'primaryIndustryGroup',
    defaultVisible: true,
    icon: 'workspaces',
    dataType: 'string',
  },
  {
    id: 'verticals',
    header: Industry_Verticals,
    accessorKey: 'verticals',
    defaultVisible: true,
    icon: 'domain',
    dataType: 'string',
  },
  {
    id: 'descriptions',
    header: 'Descriptions',
    accessorKey: 'descriptions',
    defaultVisible: true,
    icon: 'description',
    dataType: 'string',
  },
  {
    id: 'xue',
    header: 'Revenue',
    accessorKey: 'revenue',
    defaultVisible: true,
    icon: 'trending_up',
    dataType: 'money',
  },
  {
    id: 'grossProfit',
    header: 'Gross Profit',
    accessorKey: 'grossProfit',
    defaultVisible: true,
    icon: 'trending_up',
    dataType: 'money',
  },
  {
    id: 'yearFounded',
    header: 'Year Founded',
    accessorKey: 'yearFounded',
    defaultVisible: true,
    icon: 'calendar_month',
    dataType: 'number',
  },
  {
    id: 'website',
    header: 'Website',
    accessorKey: 'website',
    defaultVisible: true,
    icon: 'web',
    dataType: 'url',
  },
  {
    id: 'ebitda',
    header: 'EBITDA',
    accessorKey: 'ebitda',
    defaultVisible: true,
    icon: 'assessment',
    dataType: 'number',
  },
  {
    id: 'companyFinancingStatus',
    header: 'Company Financing Status',
    accessorKey: 'companyFinancingStatus',
    defaultVisible: true,
    icon: 'credit_score',
    dataType: 'string',
  },
  {
    id: 'ownershipStatus',
    header: 'Ownership Status',
    accessorKey: 'ownershipStatus',
    defaultVisible: true,
    icon: 'people',
    dataType: 'string',
  },
  {
    id: 'hqLocation',
    header: 'HQ Location',
    accessorKey: 'hqLocation',
    defaultVisible: true,
    icon: 'location_on',
    dataType: 'string',
  },
  {
    id: 'hqPhone',
    header: 'HQ Phone',
    accessorKey: 'hqPhone',
    defaultVisible: false,
    icon: 'phone',
    dataType: 'string',
  },
  {
    id: 'hqEmail',
    header: 'HQ Email',
    accessorKey: 'hqEmail',
    defaultVisible: false,
    icon: 'email',
    dataType: 'string',
  },
  {
    id: 'dealsDate',
    header: 'Deals Date',
    accessorKey: 'dealsDate',
    defaultVisible: false,
    icon: 'event',
    dataType: 'string',
  },
  {
    id: 'dealsSize',
    header: 'Deals Size',
    accessorKey: 'dealsSize',
    defaultVisible: false,
    icon: 'attach_money',
    dataType: 'money',
  },
  {
    id: 'dealsType',
    header: 'Deals Type',
    accessorKey: 'dealsType',
    defaultVisible: false,
    icon: 'handshake',
    dataType: 'string',
  },
  {
    id: 'dealsStatus',
    header: 'Deals Status',
    accessorKey: 'dealsStatus',
    defaultVisible: false,
    icon: 'pending',
    dataType: 'string',
  },
  {
    id: 'postValuation',
    header: 'Post Valuation',
    accessorKey: 'postValuation',
    defaultVisible: false,
    icon: 'assessment',
    dataType: 'money',
  },
  {
    id: 'acquirerInfo',
    header: 'Exit Info',
    accessorKey: 'acquirerInfo',
    defaultVisible: false,
    icon: 'event',
    dataType: 'multi',
  },
];

export const COMPANY_COLUMNS_CRUNCHBASE: ICompanyColumn[] = [
  {
    id: 'companyName',
    header: 'Company Name',
    accessorKey: 'companyName',
    defaultVisible: true,
    icon: 'business',
    dataType: 'string',
  },
  {
    id: 'domain',
    header: 'Domain',
    accessorKey: 'domain',
    defaultVisible: true,
    icon: 'web',
    dataType: 'url',
  },
  {
    id: 'lastKnownState',
    header: 'Series (Last Known)',
    accessorKey: 'lastKnownState',
    defaultVisible: true,
    icon: 'credit_score',
    dataType: 'string',
  },
  {
    id: 'orgType',
    header: 'Org Type',
    accessorKey: 'orgType',
    defaultVisible: true,
    icon: 'business',
    dataType: 'string',
  },
  {
    id: 'revenue',
    header: 'Revenue',
    accessorKey: 'revenue',
    defaultVisible: true,
    icon: 'store',
    dataType: 'money',
  },
  {
    id: 'startDate',
    header: 'Founded Date',
    accessorKey: 'startDate',
    defaultVisible: true,
    icon: 'calendar_month',
    dataType: 'date',
  },
  {
    id: 'verticals',
    header: Industry_Verticals,
    accessorKey: 'verticals', // TODO: change to verticals
    defaultVisible: true,
    icon: 'category',
    dataType: 'multi',
  },
  {
    id: 'lastFundingDate',
    header: 'Last Funding Date',
    accessorKey: 'lastFundingDate',
    defaultVisible: true,
    icon: 'language',
    dataType: 'date',
  },
  {
    id: 'lastFundingSize',
    header: 'Last Funding Size',
    accessorKey: 'lastFundingSize',
    defaultVisible: true,
    icon: 'attach_money',
    dataType: 'money',
  },
  {
    id: 'lastFundingType',
    header: 'Last Funding Type',
    accessorKey: 'lastFundingType',
    defaultVisible: true,
    icon: 'handshake',
    dataType: 'string',
  },
  {
    id: 'lastFundingInvestor',
    header: 'Last Funding Investor',
    accessorKey: 'lastFundingInvestor',
    defaultVisible: true,
    icon: 'people',
    dataType: 'string',
  },
  {
    id: 'hqLocation',
    header: 'HQ Location',
    accessorKey: 'hqLocation',
    defaultVisible: true,
    icon: 'location_on',
    dataType: 'string',
  },
  {
    id: 'acquirerInfo',
    header: 'Exit Info',
    accessorKey: 'acquirerInfo',
    defaultVisible: false,
    icon: 'event',
    dataType: 'multi',
  },
  {
    id: 'subsidiaryCompanies',
    header: 'Subsidiary Companies',
    accessorKey: 'subsidiaryCompanies',
    defaultVisible: false,
    icon: 'business',
    dataType: 'string',
  },
  {
    id: 'parentCompanies',
    header: 'Parent Companies',
    accessorKey: 'parentCompanies',
    defaultVisible: false,
    icon: 'business',
    dataType: 'string',
  },
  {
    id: 'marketCap',
    header: 'Market Cap',
    accessorKey: 'marketCap',
    defaultVisible: true,
    icon: 'local_mall',
    dataType: 'money',
  },
  // {
  //   id: 'productCategories',
  //   header: 'Product Categories',
  //   accessorKey: 'productCategories',
  //   defaultVisible: false,
  //   icon: 'category',
  //   dataType: 'multi',
  // },
  // {
  //   id: 'competitors',
  //   header: 'Competitors',
  //   accessorKey: 'competitors',
  //   defaultVisible: false,
  //   icon: 'group',
  //   dataType: 'multi',
  // }
];

export const SIDEBAR_FILTERS: Record<string, SidebarFilter> = {
  // Industry section
  industry: {
    id: 'industry',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Industry',
    section_id: 'industry',
    enabled: true,
    icon: 'construction',
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'industries.sector',
    search_field: 'company.industries.sector'
  },

  // Industry_Verticals section
  verticals: {
    id: 'verticals',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: Industry_Verticals,
    section_id: 'verticals',
    enabled: true,
    icon: 'domain',
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'verticals',
    search_field: 'company.verticals'
  },

  // Location section
  location: {
    id: 'location',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Location',
    section_id: 'location',
    enabled: true,
    icon: 'location_on',
    filter_type: FilterType.LOCATION,
  },

  // Keywords section
  keywords: {
    id: 'keywords',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Keywords',
    section_id: 'keywords',
    enabled: true,
    icon: 'tag',
    filter_type: FilterType.MULTI_SELECT,
    search_field: 'company.keywords'
  },

  // Ownership Status section
  ownership_status: {
    id: 'ownership_status',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Ownership Status',
    section_id: 'ownership_status',
    enabled: true,
    icon: 'people',
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'ownership_status',
    search_field: 'company.ownership_status'
  },

  // Deal section
  deal: {
    id: 'deal',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Deal',
    section_id: 'deal',
    enabled: true,
    icon: 'attach_money',
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'deal',
    search_field: 'deal'
  },

  // Deal Data subsection
  deal_date: {
    id: 'deal_date',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Deal Date',
    section_id: 'deal_date',
    enabled: true,
    icon: 'attach_money',
    filter_type: FilterType.DATE_RANGE,
    kw_prefix: 'deals.deal_date',
    parent_id: 'deal',
    is_range: true,
    range_meta: {
      // Convert min_value (1990) to EPOCH time (milliseconds since Jan 1, 1970)
      min_value: new Date(1990, 0, 1).getTime(),
      // Convert max_value (current year + 1) to EPOCH time in milliseconds
      max_value: new Date(new Date().getFullYear() + 1, 0, 1).getTime(),
      step: SINGLE_RANGE_STEP,
      // Convert multiply_by to milliseconds in a year (365.25 days)
      multiply_by: Math.floor(365.25 * 24 * 60 * 60 * 1000),
      unit: NumberUnitType.year,
      sub_label: 'Year',
    }
  },

  // Deal Status subsection
  deal_status: {
    id: 'deal_status',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Deal Status',
    section_id: 'deal_status',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'deals.deal_status',
    search_field: 'company.deals.deal_status',
    parent_id: 'deal'
  },

  // Deal Type subsection
  deal_type: {
    id: 'deal_type',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Deal Type',
    section_id: 'deal_type',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'deals.deal_type',
    search_field: 'company.deals.deal_type',
    parent_id: 'deal'
  },

  // Deal Type 2 subsection
  deal_type_2: {
    id: 'deal_type_2',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Deal Type 2',
    section_id: 'deal_type_2',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'deals.deal_type2',
    search_field: 'company.deals.deal_type2',
    parent_id: 'deal'
  },

  // Deal Size subsection
  deal_size: {
    id: 'deal_size',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Deal Size',
    section_id: 'deal_size',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'deals.deal_size',
    parent_id: 'deal',
    is_range: true,
    range_meta: {
      min_value: RANGE_MIN,
      max_value: MONEY_RANGE_MAX,
      multiply_by: MILLION_UNITS,
      unit: NumberUnitType.money,
      step: MONEY_RANGE_STEP,
      sub_label: 'USD in Millions',
    }
  },

  // Company Status section
  company_status: {
    id: 'company_status',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Company Status',
    section_id: 'company_status',
    enabled: true,
    icon: 'business',
    filter_type: FilterType.MULTI_SELECT,
  },

  // Backing Status subsection
  backing_status: {
    id: 'backing_status',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Backing Status',
    section_id: 'backing_status',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'business_status',
    search_field: 'company.backing_status',
    parent_id: 'company_status'
  },

  // Business Status subsection
  business_status: {
    id: 'business_status',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Business Status',
    section_id: 'business_status',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'business_status',
    search_field: 'company.business_status',
    parent_id: 'company_status'
  },

  // Investor Name subsection
  investor_name: {
    id: 'investor_name',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Investor Name',
    section_id: 'investor_name',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'investors.name',
    search_field: 'company.investors.name',
    parent_id: 'investor'
  },

  // Primary Investor Type subsection
  primary_investor_type: {
    id: 'primary_investor_type',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Primary Investor Type',
    section_id: 'primary_investor_type',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'investors.type',
    search_field: 'company.investors.type',
    parent_id: 'investor'
  },

  // Portfolio Status subsection
  portfolio_status: {
    id: 'portfolio_status',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Portfolio Status',
    section_id: 'portfolio_status',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'investors.status',
    search_field: 'company.investors.status',
    parent_id: 'investor'
  },

  // Asset Under Management (AUM) subsection
  aum: {
    id: 'aum',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Asset Under Management (AUM)',
    section_id: 'aum',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'investors.aum',
    search_field: 'aum',
    parent_id: 'investor',
    is_range: true,
    range_meta: {
      min_value: RANGE_MIN,
      max_value: MONEY_RANGE_MAX,
      multiply_by: MILLION_UNITS,
      unit: NumberUnitType.money,
      step: MONEY_RANGE_STEP,
      sub_label: 'USD in Millions',
    }
  },

  // Investor Ownership Status subsection
  investor_ownership_status: {
    id: 'investor_ownership_status',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Ownership Status',
    section_id: 'investor_ownership_status',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'investor_ownership_status',
    search_field: 'investor.company.ownership_status',
    parent_id: 'investor'
  },

  // Financial Data section
  financial_data: {
    id: 'financial_data',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Financial Data',
    section_id: 'financial_data',
    enabled: true,
    icon: 'attach_money',
    filter_type: FilterType.MULTI_SELECT,
  },

  financial_status: {
    id: 'financial_status',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Financial Status',
    section_id: 'financial_status',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'financing_status',
    search_field: 'company.financing_status',
    parent_id: 'financial_data'
  },

  // Revenue subsection
  revenue: {
    id: 'revenue',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Revenue',
    section_id: 'revenue',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'curr_financial_info.revenue',
    parent_id: 'financial_data',
    is_range: true,
    range_meta: {
      min_value: RANGE_MIN,
      max_value: MONEY_RANGE_MAX,
      multiply_by: MILLION_UNITS,
      unit: NumberUnitType.money,
      step: MONEY_RANGE_STEP,
      sub_label: 'USD in Millions',
    }
  },

  // Net Income subsection
  net_income: {
    id: 'net_income',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Net Income',
    section_id: 'net_income',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'curr_financial_info.net_income',
    parent_id: 'financial_data',
    is_range: true,
    range_meta: {
      min_value: RANGE_MIN,
      max_value: MONEY_RANGE_MAX,
      multiply_by: MILLION_UNITS,
      unit: NumberUnitType.money,
      step: MONEY_RANGE_STEP,
      sub_label: 'USD in Millions',
    }
  },

  // Enterprise Value subsection
  enterprise_value: {
    id: 'enterprise_value',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Enterprise Value',
    section_id: 'enterprise_value',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'curr_financial_info.enterprise_value',
    parent_id: 'financial_data',
    is_range: true,
    range_meta: {
      min_value: RANGE_MIN,
      max_value: MONEY_RANGE_MAX,
      multiply_by: MILLION_UNITS,
      step: MONEY_RANGE_STEP,
      unit: NumberUnitType.money,
      sub_label: 'USD in Millions',
    }
  },

  // EBITDA subsection
  ebitda: {
    id: 'ebitda',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'EBITDA',
    section_id: 'ebitda',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'ebitda',
    search_field: 'ebitda',
    parent_id: 'financial_data',
    is_range: true,
    range_meta: {
      min_value: RANGE_MIN,
      max_value: MONEY_RANGE_MAX,
      multiply_by: MILLION_UNITS,
      step: MONEY_RANGE_STEP,
      unit: NumberUnitType.money,
      sub_label: 'USD in Millions',
    }
  },

  // Other section
  other: {
    id: 'other',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Other',
    section_id: 'other',
    enabled: true,
    icon: 'more_horiz',
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'other',
    search_field: 'other'
  },

  // # Employees subsection
  employees: {
    id: 'employees',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Employees',
    section_id: 'employees',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'curr_comp_size',
    parent_id: 'other',
    is_range: true,
    range_meta: {
      min_value: 0,
      max_value: MILLION_UNITS,
      multiply_by: SINGLE_RANGE_STEP,
      step: SINGLE_RANGE_STEP,
      unit: NumberUnitType.number,
      sub_label: 'Count',
    }
  },

  // Year Founded subsection
  year_founded: {
    id: 'year_founded',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Year Founded',
    section_id: 'year_founded',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'year_founded',
    search_field: 'year_founded',
    parent_id: 'other',
    is_range: true,
    range_meta: {
      min_value: 1800,
      max_value: new Date().getFullYear() + 1,
      step: SINGLE_RANGE_STEP,
      multiply_by: SINGLE_RANGE_STEP,
      unit: NumberUnitType.year,
      sub_label: 'Year',
    }
  }
};

// crunchbase filters
export const SIDEBAR_FILTERS_CRUNCHBASE: Record<string, SidebarFilter> = {
  // company section
  verticals: {
    id: 'verticals',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: Industry_Verticals,
    section_id: 'verticals',
    enabled: true,
    icon: 'domain',
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.industry.crunchbase_categories',
    search_field: 'company.industry.crunchbase_categories',
    parent_id: 'verticals'
  },
  company_role: {
    id: 'company_role',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Roles',
    section_id: 'company_role',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.roles',
    search_field: 'company.roles',
    parent_id: 'verticals'
  },
  business_model: {
    id: 'business_model',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Business Model',
    section_id: 'business_model',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.biz_models',
    search_field: 'company.biz_models',
    parent_id: 'verticals'
  },
  // company_stage: {
  //   id: 'company_stage',
  //   toggle_state: FilterToggleState.MUST_HAVE,
  //   title: 'Company Stage',
  //   section_id: 'company_stage',
  //   enabled: true,
  //   filter_type: FilterType.MULTI_SELECT,
  //   kw_prefix: 'company.stage',
  //   search_field: 'company.stage',
  //   parent_id: 'verticals'
  // },
  // Location section
  location: {
    id: 'location',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Location',
    section_id: 'location',
    enabled: true,
    icon: 'location_on',
    filter_type: FilterType.LOCATION,
    parent_id: 'verticals'
  },
  last_known_state: {
    id: 'last_known_state',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Series (Last Known)',
    section_id: 'last_known_state',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.last_known_state',
    search_field: 'company.last_known_state',
    parent_id: 'verticals'
  },
  company_domain: {
    id: 'company_domain',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Company Domain',
    section_id: 'company_domain',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.domain',
    search_field: 'company.domain',
    parent_id: 'verticals'
  },
  org_type: {
    id: 'org_type',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Org Type',
    section_id: 'org_type',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.org_type',
    search_field: 'company.org_type',
    parent_id: 'verticals'
  },
  start_date: {
    id: 'start_date',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Founded Date',
    section_id: 'start_date',
    enabled: true,
    filter_type: FilterType.DATE_RANGE,
    kw_prefix: 'company.start_date',
    parent_id: 'verticals',
    is_range: true,
    range_meta: {
      min_value: new Date(1900, 0, 1).getTime(),
      max_value: new Date(new Date().getFullYear(), 0, 1).getTime(),
      step: SINGLE_RANGE_STEP,
      multiply_by: Math.floor(365.25 * 24 * 60 * 60 * 1000),
      unit: NumberUnitType.year,
      sub_label: 'Year',
    }
  },
  end_date: {
    id: 'end_date',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Exit Date',
    section_id: 'end_date',
    enabled: true,
    filter_type: FilterType.DATE_RANGE,
    kw_prefix: 'company.exit_info.exit_date',
    parent_id: 'verticals',
    is_range: true,
    range_meta: {
      min_value: new Date(1900, 0, 1).getTime(),
      max_value: new Date(new Date().getFullYear(), 0, 1).getTime(),
      step: SINGLE_RANGE_STEP,
      multiply_by: Math.floor(365.25 * 24 * 60 * 60 * 1000),
      unit: NumberUnitType.year,
      sub_label: 'Year',
    }
  },
  // Keywords section
  keywords: {
    id: 'keywords',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Keywords',
    section_id: 'keywords',
    enabled: true,
    icon: 'tag',
    filter_type: FilterType.MULTI_SELECT,
    search_field: 'company.keywords',
    parent_id: 'verticals'
  },
  // funding section
  investor_name: {
    id: 'investor_name',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Investor Name',
    section_id: 'investor_name',
    enabled: true,
    icon: 'people',
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.funding.investors.investor_name',
    search_field: 'company.funding.investors.investor_name',
    parent_id: 'investor'
  },
  investor_types: {
    id: 'investor_types',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Investor Types',
    section_id: 'investor_types',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.funding.investors.investor_types',
    search_field: 'company.funding.investors.investor_types',
    parent_id: 'investor'
  },
  series: {
    id: 'series',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Series (All Time)',
    section_id: 'series',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.funding.series',
    search_field: 'company.funding.series',
    parent_id: 'financial_data'
  },
  funding_date: {
    id: 'funding_date',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Funding Date',
    section_id: 'funding_date',
    enabled: true,
    filter_type: FilterType.DATE_RANGE,
    kw_prefix: 'company.funding.date',
    parent_id: 'financial_data'
  },
  last_funding_date: {
    id: 'last_funding_date',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Last Funding Date',
    section_id: 'last_funding_date',
    enabled: true,
    filter_type: FilterType.DATE_RANGE,
    kw_prefix: 'company.curr_financial_info.last_funding_date',
    parent_id: 'financial_data'
  },
  total_funding: {
    id: 'total_funding',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Total Funding',
    section_id: 'total_funding',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'company.total_funding',
    is_range: true,
    parent_id: 'investor',
    range_meta: {
      min_value: RANGE_MIN,
      max_value: MONEY_RANGE_MAX,
      multiply_by: MILLION_UNITS,
      step: MONEY_RANGE_STEP,
      unit: NumberUnitType.money,
      sub_label: 'USD in Millions',
    }
  },
  employees: {
    id: 'employees',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Employees',
    section_id: 'employees',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'company.curr_comp_size',
    parent_id: 'other',
    is_range: true,
    range_meta: {
      min_value: 0,
      max_value: MILLION_UNITS,
      multiply_by: SINGLE_RANGE_STEP,
      step: SINGLE_RANGE_STEP,
      unit: NumberUnitType.number,
      sub_label: 'Count',
    }
  },
  // acquisition section - Updated to find acquirers using exit_info
  acquisition_date: {
    id: 'acquisition_date',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Acquisition Date',
    section_id: 'acquisition_date',
    enabled: true,
    filter_type: FilterType.DATE_RANGE,
    kw_prefix: 'company.exit_info.exit_date',
    parent_id: 'acquisition',
    is_range: true,
    range_meta: {
      // Convert min_value (1990) to EPOCH time (milliseconds since Jan 1, 1970)
      min_value: new Date(1990, 0, 1).getTime(),
      // Convert max_value (current year + 1) to EPOCH time in milliseconds
      max_value: new Date(new Date().getFullYear(), 0, 1).getTime(),
      step: SINGLE_RANGE_STEP,
      // Convert multiply_by to milliseconds in a year (365.25 days)
      multiply_by: Math.floor(365.25 * 24 * 60 * 60 * 1000),
      unit: NumberUnitType.year,
      sub_label: 'Year',
    }
  },
  acquisition_amount: {
    id: 'acquisition_amount',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Acquisition Amount',
    section_id: 'acquisition_amount',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'company.exit_info.exit_amount',
    parent_id: 'acquisition',
    is_range: true,
    range_meta: {
      min_value: RANGE_MIN,
      max_value: MONEY_RANGE_MAX,
      multiply_by: MILLION_UNITS,
      step: MONEY_RANGE_STEP,
      unit: NumberUnitType.money,
      sub_label: 'USD in Millions',
    }
  },
  acquisition_company_name: {
    id: 'acquisition_company_name',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Acquirer Company Name',
    section_id: 'acquisition_company_name',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.exit_info.acquirer_entity_name',
    search_field: 'company.exit_info.acquirer_entity_name',
    parent_id: 'acquisition'
  },
  // subsidiary section
  subsidiary_name: {
    id: 'subsidiary_name',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Subsidiary Name',
    section_id: 'subsidiary_name',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.subsidiaries.company_name',
    search_field: 'company.subsidiaries.company_name',
    parent_id: 'subsidiary'
  },
  subsidiary_domain: {
    id: 'subsidiary_domain',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Subsidiary Domain',
    section_id: 'subsidiary_domain',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.subsidiaries.company_domain',
    search_field: 'company.subsidiaries.company_domain',
    parent_id: 'subsidiary'
  },
  // parent section
  parent_name: {
    id: 'parent_name',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Parent Name',
    section_id: 'parent_name',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.parent_companies.company_name',
    search_field: 'company.parent_companies.company_name',
    parent_id: 'parent'
  },
  parent_domain: {
    id: 'parent_domain',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Parent Domain',
    section_id: 'parent_domain',
    enabled: true,
    filter_type: FilterType.MULTI_SELECT,
    kw_prefix: 'company.parent_companies.company_domain',
    search_field: 'company.parent_companies.company_domain',
    parent_id: 'parent'
  },
  // Revenue subsection
  revenue: {
    id: 'revenue',
    toggle_state: FilterToggleState.MUST_HAVE,
    title: 'Revenue',
    section_id: 'revenue',
    enabled: true,
    filter_type: FilterType.NUMBER_RANGE,
    kw_prefix: 'company.curr_financial_info.revenue',
    parent_id: 'financial_data',
    is_range: true,
    range_meta: {
      min_value: RANGE_MIN,
      max_value: MONEY_RANGE_MAX,
      multiply_by: MILLION_UNITS,
      unit: NumberUnitType.money,
      step: MONEY_RANGE_STEP,
      sub_label: 'USD in Millions',
    }
  },
}

// Main filter groups
export const COMPANY_FILTER_GROUPS = [
  {
    id: 'industry',
    title: 'Industry',
    icon: 'construction',
    fields: ['industry']
  },
  {
    id: 'verticals',
    title: Industry_Verticals,
    icon: 'domain',
    fields: ['verticals']
  },
  {
    id: 'location',
    title: 'Location',
    icon: 'location_on',
    fields: ['location']
  },
  {
    id: 'keywords',
    title: 'Keywords',
    icon: 'tag',
    fields: ['keywords']
  },
  {
    id: 'ownership_status',
    title: 'Ownership Status',
    icon: 'people',
    fields: ['ownership_status']
  },
  {
    id: 'deal',
    title: 'Deal',
    icon: 'attach_money',
    fields: ['deal_date', 'deal_status', 'deal_type', 'deal_type_2', 'deal_size']
  },
  {
    id: 'company_status',
    title: 'Company Status',
    icon: 'business',
    fields: ['backing_status', 'business_status', 'company_ownership_status']
  },
  {
    id: 'investor',
    title: 'Investor',
    icon: 'account_balance',
    fields: ['investor_name', 'primary_investor_type', 'portfolio_status', 'aum', 'investor_ownership_status']
  },
  {
    id: 'financial_data',
    title: 'Financial Data',
    icon: 'attach_money',
    fields: ['revenue']
  },
  {
    id: 'other',
    title: 'Other',
    icon: 'more_horiz',
    fields: ['employees', 'year_founded']
  }
];

export const COMPANY_FILTER_GROUPS_CRUNCHBASE = [
  {
    id: 'verticals',
    title: 'Company',
    icon: 'domain',
    fields: ['verticals', 'business_model', /* 'company_stage', */ 'company_role',
      'company_domain', 'org_type', 'start_date',
      'end_date', 'location', 'employees', 'keywords'
    ]
  },
  {
    id: 'financial_data',
    title: 'Funding',
    icon: 'attach_money',
    fields: ['last_known_state', 'series', 'funding_date', 'last_funding_date']
  },
  {
    id: 'investor',
    title: 'Investor',
    icon: 'people',
    fields: ['investor_name', 'investor_types', 'total_funding']
  },
  {
    id: 'financial_data',
    title: 'Financial Data',
    icon: 'attach_money',
    fields: ['revenue']
  },
  {
    id: 'acquisition',
    title: 'Acquisition',
    icon: 'event',
    fields: ['acquisition_date', 'acquisition_amount', 'acquisition_company_name']
  },
  {
    id: 'subsidiary',
    title: 'Subsidiary',
    icon: 'business',
    fields: ['subsidiary_name', 'subsidiary_domain']
  },
  {
    id: 'parent',
    title: 'Parent',
    icon: 'business',
    fields: ['parent_name', 'parent_domain']
  }
]




export const RRA_PITCHBOOK_VENDOR = "rra_pitchbook";
export const ALL_COMPANY_LISTS_PAGE_SIZE = 10;
export const companyDefaultLogo = "data:image/png;base64, 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"
export enum VendorType {
  PITCHBOOK = "pitchbook",
  FINDEM = "findem",
  RRA_PITCHBOOK = "rra_pitchbook",
  CRUNCHBASE = "crunchbase",
}
export const VENDORS: { [key: string]: string } = {
  [VendorType.PITCHBOOK]: "Pitchbook",
  [VendorType.FINDEM]: "Findem",
  [VendorType.RRA_PITCHBOOK]: "Pitchbook",
  [VendorType.CRUNCHBASE]: "Findem",
}

export enum CompanyListOwnerFilterValue {
  ALL = "all",
  MY = "my",
  SHARED = "shared",
}

export const QUERY_PARAMS = {
  TARGET_USER_ID: 'target_user_id',
  OWNER: 'owner',
  PAGE: 'page',
} as const;

export const OWNER_FILTER_LABELS = {
  ALL_LISTS: "All Lists",
  MY_LISTS: "My Lists",
  SHARED_WITH_ME: "Shared with Me",
} as const;

export const IMPERSONATION_LABELS = {
  SWITCH_USER_PLACEHOLDER: "Switch user",
  UNKNOWN_USER: "Unknown",
  NO_EMAIL: "No email",
} as const;
